import {
  Content,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  MenuToggleElement,
  Switch
} from '@patternfly/react-core';
import { CogIcon, MoonIcon, SignOutAltIcon, SunIcon, UserIcon } from '@patternfly/react-icons';
import { useKeycloak } from '@project/auth/keycloakInstance';
import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

const ThemeToggle: React.FC = () => {
  const { t } = useTranslation();
  
  // Initialise based on current document class or localStorage
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('pf-v6-theme-dark')
  );

  useEffect(() => {
    const handleSync = (e: any) => setIsDark(e.detail);

    // Listen for the custom event we created in MainLayout
    globalThis.addEventListener('local-theme-sync', handleSync);

    return () => globalThis.removeEventListener('local-theme-sync', handleSync);
  }, []);

  const toggleTheme = useCallback((checked: boolean) => {
    setIsDark(checked);
    const theme = checked ? 'dark' : 'light';

    if (checked) {
      document.documentElement.classList.add('pf-v6-theme-dark');
    } else {
      document.documentElement.classList.remove('pf-v6-theme-dark');
    }

    localStorage.setItem('theme', theme);

    const themeChannel = new BroadcastChannel('theme_channel');
    themeChannel.postMessage(checked);
    themeChannel.close();
  }, []);

  return (
    <DropdownItem
      component="div"
      icon={isDark ? <MoonIcon /> : <SunIcon />}
      onClick={(e) => {
        e.stopPropagation();
        toggleTheme(!isDark);
      }}
      style={{ cursor: 'pointer' }}
    >
      <Switch
        id="theme-switch"
        label={isDark ? t('userDropdown.darkTheme', 'Dark theme') : t('userDropdown.lightTheme', 'Light theme')}
        isChecked={isDark}
        onChange={() => {}}
        onClick={(e) => e.stopPropagation()}
        isReversed
        aria-label={t('userDropdown.toggleTheme', 'Toggle dark theme')}
      />
    </DropdownItem>
  );
};

const UserDropdownComponent: React.FC = () => {
  const { keycloak, logout } = useKeycloak();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const username = useMemo(() => keycloak.tokenParsed?.preferred_username || 'User', [keycloak]);
  const fullName = useMemo(() => keycloak.tokenParsed?.name || 'Guest', [keycloak]);
  const email = useMemo(() => keycloak.tokenParsed?.email || '', [keycloak]);
  const isAdmin = useMemo(() => keycloak.hasRealmRole('admin'), [keycloak]);

  const sessionExpiry = useMemo(() => {
    if (!keycloak.refreshTokenParsed?.exp) return undefined;
    const expiresAt = new Date(keycloak.refreshTokenParsed.exp * 1000);
    return t('userDropdown.sessionExpiresAt', 'Session expires at {{time}}', { time: expiresAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) });
  }, [keycloak.refreshTokenParsed, t]);

  const runWithFreshToken = useCallback(async (action: () => void) => {
    try {
      const refreshed = await keycloak.updateToken(30);
      if (refreshed) {
        console.log('Token was successfully refreshed');
      }
      action();
    } catch (error) {
      console.error('Failed to refresh token:', error);
      keycloak.login();
    }
  }, [keycloak]);

  const onToggle = useCallback(() => setIsOpen(prev => !prev), []);

  const handleLogout = useCallback(() => {
    runWithFreshToken(() => {
      logout();
    });
  }, [runWithFreshToken, logout]);

  const renderToggle = useCallback((toggleRef: React.Ref<MenuToggleElement>) => (
    <MenuToggle
      ref={toggleRef}
      onClick={onToggle}
      isExpanded={isOpen}
      variant="plain"
      icon={<UserIcon />}
    >
      {username}
    </MenuToggle>
  ), [isOpen, onToggle, username]);

  return (
    <Dropdown
      isOpen={isOpen}
      onOpenChange={(nextOpen) => setIsOpen(nextOpen)}
      popperProps={{ position: 'right' }}
      toggle={renderToggle}
      id="user-dropdown"
    >
      <DropdownList>
        <div style={{ padding: '12px 16px', minWidth: '220px' }}>
          <Content component="p" className="pf-v6-u-font-weight-bold pf-v6-u-mb-xs">
            {fullName}
          </Content>
          <Content component="p" className="pf-v6-u-font-size-sm pf-v6-u-color-200">
            {email}
          </Content>
        </div>

        <Divider component="li" />

        <DropdownItem
          key="account"
          onClick={() => {
            setIsOpen(false);
            runWithFreshToken(() => keycloak.accountManagement());
          }}
          icon={<CogIcon />}
        >
          {t('userDropdown.accountManagement', 'Account Management')}
        </DropdownItem>

        {isAdmin && (
          <DropdownItem
            key="admin"
            onClick={() => {
              setIsOpen(false);
              runWithFreshToken(() => console.log('Navigate to Admin')); // Placeholder code
            }}
          >
            {t('userDropdown.adminDashboard', 'Admin Dashboard')}
          </DropdownItem>
        )}

        <Divider component="li" />
        <ThemeToggle />
        <Divider component="li" />

        <DropdownItem
          key="logout"
          onClick={() => {
            setIsOpen(false);
            handleLogout();
          }}
          icon={<SignOutAltIcon />}
          description={sessionExpiry}
        >
          {t('userDropdown.logout', 'Logout')}
        </DropdownItem>
      </DropdownList>
    </Dropdown>
  );
};

export const UserDropdown = memo(UserDropdownComponent);
export default UserDropdown;

